import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2 } from 'lucide-react';
import resourceRoleMasterService from '../../services/resourceRoleMasterService';
import RevisionModal from '../../components/RevisionModal/RevisionModal';
import '../../components/RevisionModal/RevisionModal.css';
import { toast } from 'react-toastify';

const ResourceRoleMasterEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('basic');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [formData, setFormData] = useState({
    resourceRoleCode: '',
    resourceRoleName: '',
    resourceRoleDescription: '',
    roleCategory: 'Technical',
    roleLevel: 'Mid-level',
    resourceRoleStatus: 'Active',
    requiredSkills: [],
    preferredSkills: [],
    minimumExperience: 0,
    maximumExperience: 5,
    educationRequirements: [],
    certifications: [],
    workType: 'Full-time',
    workLocation: 'Hybrid',
    availability: 'Immediate',
    salaryRange: {
      min: 0,
      max: 0,
      currency: 'INR',
      period: 'Monthly'
    },
    benefits: [],
    reportsTo: '',
    managesRoles: [],
    teamSize: {
      min: 0,
      max: 0
    },
    suitableProjectTypes: [],
    suitableProjectSizes: [],
    billableRate: 0,
    utilizationTarget: 80,
    careerPath: []
  });

  const [newSkill, setNewSkill] = useState({ skillName: '', skillLevel: 'Intermediate', isMandatory: true });
  const [newPreferredSkill, setNewPreferredSkill] = useState({ skillName: '', skillLevel: 'Intermediate' });
  const [newCertification, setNewCertification] = useState({ certificationName: '', isRequired: false, validityPeriod: 0 });
  const [newBenefit, setNewBenefit] = useState('');
  const [newManagesRole, setNewManagesRole] = useState('');
  const [newCareerPath, setNewCareerPath] = useState({ nextRole: '', timeToPromotion: 0, requirements: '' });

  const categoryOptions = [
    'Technical', 'Management', 'Design', 'Quality Assurance', 
    'DevOps', 'Business', 'Support', 'Other'
  ];

  const levelOptions = [
    'Junior', 'Mid-level', 'Senior', 'Lead', 
    'Principal', 'Architect', 'Manager', 'Director'
  ];

  const statusOptions = ['Active', 'Inactive', 'Deprecated', 'Legacy'];

  const skillLevelOptions = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  const educationOptions = [
    'High School', 'Diploma', 'Bachelor\'s Degree', 'Master\'s Degree', 
    'PhD', 'Certification', 'No Formal Education Required'
  ];

  const workTypeOptions = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Intern', 'Consultant'];

  const workLocationOptions = ['On-site', 'Remote', 'Hybrid', 'Any'];

  const availabilityOptions = ['Immediate', '2 Weeks', '1 Month', '2 Months', '3 Months', 'Negotiable'];

  const currencyOptions = ['INR', 'USD', 'EUR', 'GBP'];

  const periodOptions = ['Hourly', 'Daily', 'Monthly', 'Annually', 'Project-based'];

  const projectTypeOptions = [
    'Web Development', 'Mobile Development', 'Desktop Application', 'API Development',
    'Data Science', 'AI/ML', 'DevOps', 'Cloud Migration', 'Legacy Modernization'
  ];

  const projectSizeOptions = ['Small', 'Medium', 'Large', 'Enterprise'];

  useEffect(() => {
    if (location.state?.resourceRoleData) {
      setFormData(location.state.resourceRoleData);
      setInitialLoading(false);
    } else {
      fetchResourceRoleMaster();
    }
  }, [id, location.state]);

  const fetchResourceRoleMaster = async () => {
    try {
      setInitialLoading(true);
      const response = await resourceRoleMasterService.getResourceRoleMasterById(id);
      if (response.success) {
        setFormData(response.data);
      }
    } catch (error) {
      console.error('Error fetching resource role master:', error);
      toast.error('Failed to fetch resource role master');
      navigate('/projects/resource-role-master');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNestedInputChange = (parentField, childField, value) => {
    setFormData(prev => ({
      ...prev,
      [parentField]: {
        ...prev[parentField],
        [childField]: value
      }
    }));
  };

  const handleArrayInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addRequiredSkill = () => {
    if (newSkill.skillName.trim()) {
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...prev.requiredSkills, { ...newSkill }]
      }));
      setNewSkill({ skillName: '', skillLevel: 'Intermediate', isMandatory: true });
    }
  };

  const removeRequiredSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
    }));
  };

  const addPreferredSkill = () => {
    if (newPreferredSkill.skillName.trim()) {
      setFormData(prev => ({
        ...prev,
        preferredSkills: [...prev.preferredSkills, { ...newPreferredSkill }]
      }));
      setNewPreferredSkill({ skillName: '', skillLevel: 'Intermediate' });
    }
  };

  const removePreferredSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      preferredSkills: prev.preferredSkills.filter((_, i) => i !== index)
    }));
  };

  const addCertification = () => {
    if (newCertification.certificationName.trim()) {
      setFormData(prev => ({
        ...prev,
        certifications: [...prev.certifications, { ...newCertification }]
      }));
      setNewCertification({ certificationName: '', isRequired: false, validityPeriod: 0 });
    }
  };

  const removeCertification = (index) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, newBenefit]
      }));
      setNewBenefit('');
    }
  };

  const removeBenefit = (index) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index)
    }));
  };

  const addManagesRole = () => {
    if (newManagesRole.trim()) {
      setFormData(prev => ({
        ...prev,
        managesRoles: [...prev.managesRoles, newManagesRole]
      }));
      setNewManagesRole('');
    }
  };

  const removeManagesRole = (index) => {
    setFormData(prev => ({
      ...prev,
      managesRoles: prev.managesRoles.filter((_, i) => i !== index)
    }));
  };

  const addCareerPath = () => {
    if (newCareerPath.nextRole.trim()) {
      setFormData(prev => ({
        ...prev,
        careerPath: [...prev.careerPath, { ...newCareerPath }]
      }));
      setNewCareerPath({ nextRole: '', timeToPromotion: 0, requirements: '' });
    }
  };

  const removeCareerPath = (index) => {
    setFormData(prev => ({
      ...prev,
      careerPath: prev.careerPath.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.resourceRoleCode.trim() || !formData.resourceRoleName.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Store the form data and show revision modal
    setPendingFormData(formData);
    setShowRevisionModal(true);
  };

  const handleRevisionSave = async (revisionInfo) => {
    try {
      setLoading(true);
      const updateData = {
        ...pendingFormData,
        changeReason: revisionInfo.reasonForRevision
      };
      
      await resourceRoleMasterService.updateResourceRoleMaster(id, updateData);
      toast.success('Resource role master updated successfully');
      navigate('/projects/resource-role-master');
    } catch (error) {
      console.error('Error updating resource role master:', error);
      toast.error('Failed to update resource role master');
    } finally {
      setLoading(false);
      setShowRevisionModal(false);
    }
  };


  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: '📋' },
    { id: 'skills', label: 'Skills & Experience', icon: '🎯' },
    { id: 'work', label: 'Work Details', icon: '💼' },
    { id: 'compensation', label: 'Compensation', icon: '💰' },
    { id: 'career', label: 'Career Path', icon: '📈' }
  ];

  if (initialLoading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <button
            className="btn btn-outline-secondary me-3"
            onClick={() => navigate('/projects/resource-role-master')}
          >
            <ArrowLeft size={20} className="me-2" />
            Back
          </button>
          <h1 className="h3 mb-0 d-inline">Edit Resource Role Master</h1>
        </div>
        <button
          type="submit"
          form="resourceRoleForm"
          className="btn btn-primary"
          disabled={loading}
        >
          <Save size={20} className="me-2" />
          {loading ? 'Saving...' : 'Update Resource Role'}
        </button>
      </div>

      <form id="resourceRoleForm" onSubmit={handleSubmit}>
        {/* Tabs */}
        <div className="card shadow mb-4">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs">
              {tabs.map(tab => (
                <li className="nav-item" key={tab.id}>
                  <button
                    type="button"
                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    <span className="me-2">{tab.icon}</span>
                    {tab.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="card-body">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Resource Role Code *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.resourceRoleCode}
                      onChange={(e) => handleInputChange('resourceRoleCode', e.target.value.toUpperCase())}
                      placeholder="e.g., DEV-SR"
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Resource Role Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.resourceRoleName}
                      onChange={(e) => handleInputChange('resourceRoleName', e.target.value)}
                      placeholder="e.g., Senior Developer"
                      required
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.resourceRoleDescription}
                      onChange={(e) => handleInputChange('resourceRoleDescription', e.target.value)}
                      placeholder="Describe the role responsibilities and requirements..."
                    />
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Category *</label>
                    <select
                      className="form-select"
                      value={formData.roleCategory}
                      onChange={(e) => handleInputChange('roleCategory', e.target.value)}
                      required
                    >
                      {categoryOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Level *</label>
                    <select
                      className="form-select"
                      value={formData.roleLevel}
                      onChange={(e) => handleInputChange('roleLevel', e.target.value)}
                      required
                    >
                      {levelOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.resourceRoleStatus}
                      onChange={(e) => handleInputChange('resourceRoleStatus', e.target.value)}
                    >
                      {statusOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Skills & Experience Tab */}
            {activeTab === 'skills' && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-4">
                    <h6>Required Skills</h6>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Skill name"
                        value={newSkill.skillName}
                        onChange={(e) => setNewSkill(prev => ({ ...prev, skillName: e.target.value }))}
                      />
                      <select
                        className="form-select"
                        style={{ width: '150px' }}
                        value={newSkill.skillLevel}
                        onChange={(e) => setNewSkill(prev => ({ ...prev, skillLevel: e.target.value }))}
                      >
                        {skillLevelOptions.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newSkill.isMandatory}
                          onChange={(e) => setNewSkill(prev => ({ ...prev, isMandatory: e.target.checked }))}
                        />
                        <label className="form-check-label">Required</label>
                      </div>
                      <button type="button" className="btn btn-outline-primary" onClick={addRequiredSkill}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.requiredSkills.map((skill, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{skill.skillName}</strong> - {skill.skillLevel}
                            {skill.isMandatory && <span className="badge bg-danger ms-2">Required</span>}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeRequiredSkill(index)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-4">
                    <h6>Preferred Skills</h6>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Skill name"
                        value={newPreferredSkill.skillName}
                        onChange={(e) => setNewPreferredSkill(prev => ({ ...prev, skillName: e.target.value }))}
                      />
                      <select
                        className="form-select"
                        style={{ width: '150px' }}
                        value={newPreferredSkill.skillLevel}
                        onChange={(e) => setNewPreferredSkill(prev => ({ ...prev, skillLevel: e.target.value }))}
                      >
                        {skillLevelOptions.map(level => (
                          <option key={level} value={level}>{level}</option>
                        ))}
                      </select>
                      <button type="button" className="btn btn-outline-primary" onClick={addPreferredSkill}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.preferredSkills.map((skill, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{skill.skillName}</strong> - {skill.skillLevel}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removePreferredSkill(index)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Minimum Experience (years)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.minimumExperience}
                      onChange={(e) => handleInputChange('minimumExperience', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Maximum Experience (years)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.maximumExperience}
                      onChange={(e) => handleInputChange('maximumExperience', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Education Requirements</label>
                    <select
                      className="form-select"
                      multiple
                      value={formData.educationRequirements}
                      onChange={(e) => handleArrayInputChange('educationRequirements', Array.from(e.target.selectedOptions, option => option.value))}
                    >
                      {educationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <small className="form-text text-muted">Hold Ctrl/Cmd to select multiple options</small>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <h6>Certifications</h6>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Certification name"
                        value={newCertification.certificationName}
                        onChange={(e) => setNewCertification(prev => ({ ...prev, certificationName: e.target.value }))}
                      />
                      <input
                        type="number"
                        className="form-control"
                        style={{ width: '120px' }}
                        placeholder="Validity (months)"
                        min="0"
                        value={newCertification.validityPeriod}
                        onChange={(e) => setNewCertification(prev => ({ ...prev, validityPeriod: parseInt(e.target.value) || 0 }))}
                      />
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newCertification.isRequired}
                          onChange={(e) => setNewCertification(prev => ({ ...prev, isRequired: e.target.checked }))}
                        />
                        <label className="form-check-label">Required</label>
                      </div>
                      <button type="button" className="btn btn-outline-primary" onClick={addCertification}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.certifications.map((cert, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <div>
                            <strong>{cert.certificationName}</strong>
                            {cert.isRequired && <span className="badge bg-danger ms-2">Required</span>}
                            {cert.validityPeriod > 0 && <span className="badge bg-info ms-2">{cert.validityPeriod} months</span>}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeCertification(index)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Work Details Tab */}
            {activeTab === 'work' && (
              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Work Type</label>
                    <select
                      className="form-select"
                      value={formData.workType}
                      onChange={(e) => handleInputChange('workType', e.target.value)}
                    >
                      {workTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Work Location</label>
                    <select
                      className="form-select"
                      value={formData.workLocation}
                      onChange={(e) => handleInputChange('workLocation', e.target.value)}
                    >
                      {workLocationOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="mb-3">
                    <label className="form-label">Availability</label>
                    <select
                      className="form-select"
                      value={formData.availability}
                      onChange={(e) => handleInputChange('availability', e.target.value)}
                    >
                      {availabilityOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Reports To</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.reportsTo}
                      onChange={(e) => handleInputChange('reportsTo', e.target.value)}
                      placeholder="e.g., Tech Lead"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Billable Rate ($/hour)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      step="0.01"
                      value={formData.billableRate}
                      onChange={(e) => handleInputChange('billableRate', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Utilization Target (%)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max="100"
                      value={formData.utilizationTarget}
                      onChange={(e) => handleInputChange('utilizationTarget', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Team Size (Min)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.teamSize.min}
                      onChange={(e) => handleNestedInputChange('teamSize', 'min', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Team Size (Max)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.teamSize.max}
                      onChange={(e) => handleNestedInputChange('teamSize', 'max', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Suitable Project Types</label>
                    <select
                      className="form-select"
                      multiple
                      value={formData.suitableProjectTypes}
                      onChange={(e) => handleArrayInputChange('suitableProjectTypes', Array.from(e.target.selectedOptions, option => option.value))}
                    >
                      {projectTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <small className="form-text text-muted">Hold Ctrl/Cmd to select multiple options</small>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Suitable Project Sizes</label>
                    <select
                      className="form-select"
                      multiple
                      value={formData.suitableProjectSizes}
                      onChange={(e) => handleArrayInputChange('suitableProjectSizes', Array.from(e.target.selectedOptions, option => option.value))}
                    >
                      {projectSizeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    <small className="form-text text-muted">Hold Ctrl/Cmd to select multiple options</small>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <h6>Manages Roles</h6>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Role name"
                        value={newManagesRole}
                        onChange={(e) => setNewManagesRole(e.target.value)}
                      />
                      <button type="button" className="btn btn-outline-primary" onClick={addManagesRole}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.managesRoles.map((role, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{role}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeManagesRole(index)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Compensation Tab */}
            {activeTab === 'compensation' && (
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Min Salary</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.salaryRange.min}
                      onChange={(e) => handleNestedInputChange('salaryRange', 'min', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Max Salary</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={formData.salaryRange.max}
                      onChange={(e) => handleNestedInputChange('salaryRange', 'max', parseInt(e.target.value) || 0)}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Currency</label>
                    <select
                      className="form-select"
                      value={formData.salaryRange.currency}
                      onChange={(e) => handleNestedInputChange('salaryRange', 'currency', e.target.value)}
                    >
                      {currencyOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">Period</label>
                    <select
                      className="form-select"
                      value={formData.salaryRange.period}
                      onChange={(e) => handleNestedInputChange('salaryRange', 'period', e.target.value)}
                    >
                      {periodOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-12">
                  <div className="mb-3">
                    <h6>Benefits</h6>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Benefit name"
                        value={newBenefit}
                        onChange={(e) => setNewBenefit(e.target.value)}
                      />
                      <button type="button" className="btn btn-outline-primary" onClick={addBenefit}>
                        <Plus size={16} />
                      </button>
                    </div>
                    <div className="list-group">
                      {formData.benefits.map((benefit, index) => (
                        <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                          <span>{benefit}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => removeBenefit(index)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Career Path Tab */}
            {activeTab === 'career' && (
              <div className="row">
                <div className="col-12">
                  <div className="mb-3">
                    <h6>Career Progression</h6>
                    <div className="row mb-2">
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Next role"
                          value={newCareerPath.nextRole}
                          onChange={(e) => setNewCareerPath(prev => ({ ...prev, nextRole: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-3">
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Time to promotion (months)"
                          min="0"
                          value={newCareerPath.timeToPromotion}
                          onChange={(e) => setNewCareerPath(prev => ({ ...prev, timeToPromotion: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                      <div className="col-md-4">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Requirements"
                          value={newCareerPath.requirements}
                          onChange={(e) => setNewCareerPath(prev => ({ ...prev, requirements: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-1">
                        <button type="button" className="btn btn-outline-primary" onClick={addCareerPath}>
                          <Plus size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="list-group">
                      {formData.careerPath.map((path, index) => (
                        <div key={index} className="list-group-item">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{path.nextRole}</strong>
                              <br />
                              <small className="text-muted">
                                Time to promotion: {path.timeToPromotion} months
                              </small>
                              <br />
                              <small className="text-muted">
                                Requirements: {path.requirements}
                              </small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeCareerPath(index)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {/* Revision Modal */}
      <RevisionModal
        isOpen={showRevisionModal}
        onClose={() => setShowRevisionModal(false)}
        onSave={handleRevisionSave}
        currentRevision={1}
      />
    </div>
  );
};

export default ResourceRoleMasterEdit;
